import React from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";

/**
 * Generic admin list: desktop table + mobile cards with optional pagination.
 */
export default function AdminSearchableList({
  items = [],
  loading = false,
  error = null,
  emptyMessage = "No items found.",
  columns = [],
  renderMobileCard,
  onRowClick,
  pagination,
  getRowKey = (item) => item._id,
}) {
  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading…</div>;
  }

  if (error) {
    return <div className="text-red-600 py-4">{error}</div>;
  }

  if (!items.length) {
    return <div className="text-sm text-gray-600 py-4">{emptyMessage}</div>;
  }

  const { page, totalPages, total, onPrev, onNext, pageSize } = pagination || {};

  return (
    <>
      <div className="space-y-3 md:hidden mb-4">
        {items.map((item) => (
          <Card key={getRowKey(item)} className="p-4">
            {renderMobileCard ? (
              renderMobileCard(item)
            ) : (
              <div
                className={onRowClick ? "cursor-pointer" : ""}
                onClick={() => onRowClick?.(item)}
                onKeyDown={(e) => e.key === "Enter" && onRowClick?.(item)}
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
              >
                {columns.map((col) => (
                  <div key={col.key} className="text-sm text-gray-700">
                    <span className="text-gray-500">{col.label}: </span>
                    {col.render ? col.render(item) : item[col.key]}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <div className="hidden md:block mb-4">
        <table className="table-auto w-full bg-white rounded shadow">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className="text-left p-3 text-sm font-medium text-gray-700">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={getRowKey(item)}
                className={`border-t ${onRowClick ? "cursor-pointer hover:bg-gray-50" : ""}`}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((col) => (
                  <td key={col.key} className="p-3 text-sm">
                    {col.render ? col.render(item) : item[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            Showing {items.length} of {total} (page {page})
          </div>
          <div>
            <Button variant="outline" size="sm" onClick={onPrev} disabled={page <= 1} className="mr-2">
              Prev
            </Button>
            <Button variant="outline" size="sm" onClick={onNext} disabled={page >= totalPages}>
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
